import { FaxQueries, FaxTypes } from '@frontend/api-fax';
import { InfinitePaginatedList } from '@frontend/components';
import { Header } from '@frontend/header-navigation';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import {
  Button,
  ContentPanel,
  SearchField,
  useDebouncedValue,
  useFormField,
  useModalControl,
} from '@frontend/design-system';
import { ContactListItem } from '../components/contacts/contact-list-item';
import { LocationSelectorModal } from '../components/inbox/location-selector-modal';

type Props = {
  type?: FaxTypes.InboxType;
};

export const FaxContactList = ({ type = FaxTypes.InboxType.CONTACTS }: Props) => {
  const { t } = useTranslation('fax');
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const { selectedLocationIds } = useAppScopeStore();
  const searchContactProps = useFormField({
    type: 'text',
  });

  const debouncedSearchTerm = useDebouncedValue(searchContactProps.value);

  const { setShow } = useSlidePanelShallowStore('setShow');

  const { modalProps: locationSelectorModalProps, openModal: openLocationSelectorModal } = useModalControl();

  const paginatedSearchContactsInfiniteQuery = FaxQueries.useSearchContactsInfiniteQuery({
    locationId: selectedLocationIds[0],
    locationIds: selectedLocationIds,
    query: debouncedSearchTerm,
    enabled: type === FaxTypes.InboxType.CONTACTS && debouncedSearchTerm.length > 0,
  });

  const paginatedListContactsInfiniteQuery = FaxQueries.useListContactsInfiniteQuery({
    locationId: selectedLocationIds[0],
    locationIds: selectedLocationIds,
    type,
    enabled: type === FaxTypes.InboxType.CONTACTS,
  });

  const handleLocationOnClick = (locationId: string) => {
    setShow(true, 'faxContactForm', { locationId, action: FaxTypes.ContactAction.ADD });
  };

  return (
    <>
      <ContentPanel.Header>
        <div
          css={{
            display: 'flex',
            gap: theme.spacing(1),
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Header title={t('Fax Contacts')} data-trackingid={`${FaxPrefixes.List}-title-button`} />
          <Button
            iconName='plus'
            trackingId={`${FaxPrefixes.List}-create-contact-button`}
            css={{ width: 'auto' }}
            size='large'
            onClick={() => {
              if (selectedLocationIds.length > 1) {
                openLocationSelectorModal();
              } else {
                setShow(true, 'faxContactForm', {
                  locationId: selectedLocationIds[0],
                  action: FaxTypes.ContactAction.ADD,
                });
              }
            }}
          >
            {isMobile ? null : t('New Contact')}
          </Button>
        </div>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(2),
            paddingTop: theme.spacing(3),
          }}
        >
          <SearchField {...searchContactProps} name='search-contact' />
        </div>
      </ContentPanel.Header>
      <ContentPanel.Body>
        <InfinitePaginatedList
          infiniteQueryProps={
            debouncedSearchTerm.length > 0 ? paginatedSearchContactsInfiniteQuery : paginatedListContactsInfiniteQuery
          }
          emptyStateConfig={{
            header: t('No contacts to show'),
            type: 'fax_contacts',
          }}
          renderListItem={({ listItem }) => <ContactListItem listItem={listItem} />}
          endOfListText=''
        />
      </ContentPanel.Body>
      <LocationSelectorModal
        modalProps={locationSelectorModalProps}
        locationIds={selectedLocationIds}
        onClick={handleLocationOnClick}
        isCreateContact={true}
      />
    </>
  );
};
